import React, { useState, useRef, useEffect, CSSProperties } from 'react';
import { useLabels, useEditMode } from '../config/labelsContext';

// Get value from nested object by dot path.
function getByPath(obj: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

interface EditablePlaceholderProps {
  labelKey: string;
  defaultValue: string;
  children: (placeholderValue: string) => React.ReactNode;
}

/**
 * Wraps an input/textarea and makes its placeholder editable in edit mode.
 * Usage:
 *   <EditablePlaceholder labelKey="phase1.field1Placeholder" defaultValue="記入例：...">
 *     {(placeholder) => <textarea placeholder={placeholder} ... />}
 *   </EditablePlaceholder>
 */
const EditablePlaceholder: React.FC<EditablePlaceholderProps> = ({
  labelKey,
  defaultValue,
  children,
}) => {
  const { labels, updateLabel } = useLabels();
  const isEditMode = useEditMode();

  const currentValue =
    getByPath(labels as unknown as Record<string, unknown>, labelKey) ?? defaultValue;
  const isModified = currentValue !== defaultValue;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(currentValue);
  }, [currentValue]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleConfirm = () => {
    setEditing(false);
    if (draft !== currentValue) {
      updateLabel(labelKey, draft);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') {
      setDraft(currentValue);
      setEditing(false);
    }
  };

  if (!isEditMode) {
    return <>{children(currentValue)}</>;
  }

  const editBtnStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    fontSize: 11,
    color: isModified ? '#d97706' : '#6b7280',
    background: isModified ? '#fffbeb' : '#f3f4f6',
    border: `1px solid ${isModified ? '#fbbf24' : '#d1d5db'}`,
    borderRadius: 4,
    padding: '2px 6px',
    marginTop: 4,
  };

  return (
    <div>
      {children(currentValue)}
      {editing ? (
        <div style={{ marginTop: 4 }}>
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleConfirm}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              fontSize: 12,
              padding: '4px 6px',
              border: `2px solid ${isModified ? '#f59e0b' : '#3b82f6'}`,
              borderRadius: 4,
              boxSizing: 'border-box',
              background: isModified ? '#fffbeb' : '#fff',
            }}
          />
        </div>
      ) : (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setEditing(true);
          }}
          style={editBtnStyle}
        >
          ✏️ placeholder:{' '}
          {currentValue.length > 30 ? currentValue.slice(0, 30) + '...' : currentValue}
        </div>
      )}
    </div>
  );
};

export default EditablePlaceholder;
